/**
 * Progetti sul telefono (7/10/2026): risponde a "cosa stiamo consegnando, e
 * quando scade?".
 *
 * Prima, a 390px: quattro numeri in griglia (spesso a zero: "In ritardo 0"),
 * una frase di quattro righe sui progetti senza data, le linguette delle
 * divisioni, poi schede alte mezzo schermo con DIVISIONE / A CHE PUNTO È /
 * VALORE / CONSEGNA: se ne vedeva una e mezza, e somigliavano a quelle delle
 * trattative e dei contatti.
 * Qui la forma è quella di un calendario delle consegne:
 *   1. in grande, quanti lavori abbiamo in mano, quanto valgono e se qualcuno
 *      è in ritardo;
 *   2. i progetti raggruppati per QUANDO consegnano (in ritardo, entro 30
 *      giorni, più avanti, senza data), con la data in testa alla riga: per
 *      chi è, che cos'è e a che punto è, il valore a destra;
 *   3. chiusi e consegne di Design come viste, solo se ce ne sono.
 * Stesse chiamate e stessi parametri nell'indirizzo del computer (divisione,
 * cliente, q, stato, pm, consegna, vista): un link copiato da una funziona
 * nell'altra. La bacheca e l'apertura di un progetto restano al computer.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur } from '../../lib/utils';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, useFiltri, type FiltroDef } from '../../components/ui/OrdinaFiltra';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MSearch, MSeg, leggibile } from '../../components/mobile/MobileKit';
import { DIVISION_LABEL, Division, ProjectStatus } from '@masamune/shared';
import {
  type ProjectRow,
  type ProjectFilters,
  type DeliverableRow,
  type FiltroConsegna,
  canWriteProjects,
  canReadDeliverables,
  projectsBoardQuery,
  projectsDivisionsQuery,
  deliverablesQuery,
  projectClientsQuery,
  projectPmsQuery,
} from './projectsApi';
import './progetti-mobile.css';

type Vista = 'corso' | 'chiusi' | 'consegne';

const TUTTE = 'tutte';

/** Gli stati che un progetto vivo può avere, detti come si dicono. */
export const STATO_PROGETTO: Record<string, string> = {
  PREVENTIVO: 'In preventivo',
  ATTIVO: 'Attivo',
  IN_CORSO: 'In corso',
  REVISIONE: 'In revisione',
  IN_PAUSA: 'In pausa',
  CHIUSO: 'Chiuso',
};

const FILTRO_CONSEGNA: Array<{ value: Exclude<FiltroConsegna, ''>; label: string }> = [
  { value: 'in-ritardo', label: 'Solo i progetti in ritardo' },
  { value: 'entro-30-giorni', label: 'Solo i progetti da consegnare entro 30 giorni' },
  { value: 'senza-data', label: 'Solo i progetti senza data di consegna' },
];
const CONSEGNE_VALIDE = new Set<string>(FILTRO_CONSEGNA.map((o) => o.value));
/** Le stesse voci di "Filtra per" del computer. */
const FILTRO_STATO: Record<string, string> = {
  PREVENTIVO: 'Solo i progetti ancora in preventivo',
  ATTIVO: 'Solo i progetti attivi',
  IN_CORSO: 'Solo i progetti in corso',
  REVISIONE: 'Solo i progetti in revisione',
  IN_PAUSA: 'Solo i progetti in pausa',
};
const STATI_VIVI = [ProjectStatus.PREVENTIVO, ProjectStatus.ATTIVO, ProjectStatus.IN_CORSO, ProjectStatus.REVISIONE, ProjectStatus.IN_PAUSA];

// ── Le date, come le conta il server (oggi a Roma, a mezzanotte UTC) ──────

/** Oggi a Roma, a mezzanotte UTC: le scadenze arrivano scritte così. */
export function oggiRoma(): number {
  const iso = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' });
  return Date.parse(`${iso}T00:00:00Z`);
}

/** Giorni da oggi a una data (negativi se è passata). */
export function giorniA(iso: string): number {
  return Math.round((Date.parse(iso) - oggiRoma()) / 86_400_000);
}

/**
 * La data nel quadratino in testa alla riga, su due righe: "31 / ott" se è
 * quest'anno, "ago / 2027" se è un altro anno (così lontano conta il mese, non
 * il giorno).
 */
export function dataInTesta(iso: string): string {
  const d = new Date(iso);
  const mese = d.toLocaleDateString('it-IT', { month: 'short', timeZone: 'UTC' }).replace('.', '');
  const anno = d.getUTCFullYear();
  const questAnno = new Date(oggiRoma()).getUTCFullYear();
  return anno === questAnno ? `${d.getUTCDate()}\n${mese}` : `${mese}\n${anno}`;
}

/** "31 ottobre", "31 agosto 2027". */
export function dataParlata(iso: string): string {
  const d = new Date(iso);
  const questAnno = new Date(oggiRoma()).getUTCFullYear();
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', ...(d.getUTCFullYear() !== questAnno ? { year: 'numeric' } : {}), timeZone: 'UTC' });
}

/**
 * Che cos'è il lavoro, senza ripetere il cliente: "New Omnia — Social media,
 * ADV e DEM 2025/26" diventa "Social media, ADV e DEM 2025/26" (il cliente è
 * già il titolo della riga). Senza il trattino lungo il nome resta intero.
 */
export function cosaE(nome: string): string {
  const i = nome.indexOf(' — ');
  return i > 0 ? nome.slice(i + 3).trim() || nome : nome;
}

export const nomeCliente = (c: { shortName: string; legalName: string }) => leggibile(c.shortName || c.legalName);

const euro = (n: number) => `€ ${fmtEur(n)}`;
const plurale = (n: number, uno: string, molti: string) => `${n} ${n === 1 ? uno : molti}`;

export function ProgettiMobile({ initialDivision, initialVista }: { initialDivision?: Division; initialVista?: 'lista' | 'bacheca' | 'consegne' }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteProjects(user);
  const vedeConsegne = canReadDeliverables(user);

  const [params, setParams] = useSearchParams();
  const vistaParam = params.get('vista') ?? initialVista ?? 'lista';
  const statoParam = params.get('stato') ?? '';
  const vista: Vista = vistaParam === 'consegne' ? 'consegne' : statoParam === ProjectStatus.CHIUSO ? 'chiusi' : 'corso';

  // Stessa regola del computer per la divisione sui vecchi indirizzi
  // (/divisions/marketing): "tutte" si scrive per esteso invece di cancellare.
  const [divisioneParam, setDivisioneParam] = useUrlState<string>('divisione', initialDivision ?? '');
  const divisione = divisioneParam === TUTTE ? '' : divisioneParam;
  const setDivisione = (d: string) => setDivisioneParam(d === '' && initialDivision ? TUTTE : d);
  const [clientId, setClientId] = useUrlState<string>('cliente', '');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });

  // Il vecchio `ritardo=1` si traduce una volta sola, come sul computer.
  const ritardoVecchio = params.get('ritardo') === '1';
  useEffect(() => {
    if (!ritardoVecchio) return;
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('ritardo');
      if (!sp.get('consegna')) sp.set('consegna', 'in-ritardo');
      return sp;
    }, { replace: true });
  }, [ritardoVecchio, setParams]);

  const pmQ = useQuery(projectPmsQuery);
  const pms = pmQ.data?.items;
  const defsFiltri = useMemo<FiltroDef[]>(() => [
    { param: 'consegna', label: 'Consegna', opzioni: FILTRO_CONSEGNA },
    ...(pms?.length ? [{ param: 'pm', label: 'Chi lo segue', opzioni: pms.map((u) => ({ value: u.id, label: `Seguiti da ${u.firstName} ${u.lastName}` })) }] : []),
    // "Chiuso" non c'è: i chiusi sono una vista, qui sopra.
    { param: 'stato', label: 'A che punto è', opzioni: STATI_VIVI.map((s) => ({ value: s, label: FILTRO_STATO[s]! })) },
  ], [pms]);
  const filtriUi = useFiltri(defsFiltri);
  const consegnaRaw = filtriUi.valore('consegna');
  const consegna = (CONSEGNE_VALIDE.has(consegnaRaw) ? consegnaRaw : '') as FiltroConsegna;
  const pmId = filtriUi.valore('pm');

  // Una chiamata sola per tutti gli stati (la stessa della bacheca del
  // computer, fino a 200 righe): in corso e chiusi si separano qui, così i
  // conteggi delle viste non cambiano a seconda di quella che si guarda.
  const filtri: ProjectFilters = { division: divisione, status: '', clientId, pmId, search, consegna };
  const listaQ = useQuery(projectsBoardQuery(filtri));
  const divisionsQ = useQuery(projectsDivisionsQuery(filtri));
  const consegneQ = useQuery({ ...deliverablesQuery, enabled: vedeConsegne });
  const clientiQ = useQuery({ ...projectClientsQuery, enabled: !!clientId });

  const tutti = listaQ.data?.items ?? [];
  const statoVivo = (STATI_VIVI as string[]).includes(statoParam) ? statoParam : '';
  const vivi = tutti.filter((p) => p.status !== ProjectStatus.CHIUSO && (!statoVivo || p.status === statoVivo));
  const chiusi = tutti.filter((p) => p.status === ProjectStatus.CHIUSO);
  const consegneTutte = consegneQ.data?.items ?? [];

  const scegliVista = (v: Vista) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('vista');
      if (sp.get('stato') === ProjectStatus.CHIUSO) sp.delete('stato');
      if (v === 'consegne') sp.set('vista', 'consegne');
      if (v === 'chiusi') sp.set('stato', ProjectStatus.CHIUSO);
      return sp;
    }, { replace: true });
  };

  // Le divisioni si scelgono solo se ce n'è più d'una con dei progetti: con
  // tutti i progetti nel Marketing, "Tutte 5 · Marketing 5" era la stessa
  // cosa scritta due volte.
  const conta = (d: Division) => divisionsQ.data?.items.find((i) => i.division === d)?.count ?? 0;
  const divisioniPiene = (Object.keys(DIVISION_LABEL) as Division[]).filter((d) => conta(d) > 0 || divisione === d);
  const mostraDivisioni = divisioniPiene.length > 1 || !!divisione;

  const viste: Array<{ key: Vista; label: string; count?: number }> = [
    { key: 'corso', label: 'In corso', count: listaQ.data ? tutti.filter((p) => p.status !== ProjectStatus.CHIUSO).length : undefined },
    ...(chiusi.length || vista === 'chiusi' ? [{ key: 'chiusi' as const, label: 'Chiusi', count: chiusi.length }] : []),
    ...(vedeConsegne && (consegneTutte.length || vista === 'consegne') ? [{ key: 'consegne' as const, label: 'Consegne di Design', count: consegneTutte.length }] : []),
  ];

  const filtriAccesi = filtriUi.attivi + (search.trim() ? 1 : 0) + (clientId ? 1 : 0);
  const azzera = () => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const k of ['stato', 'cliente', 'pm', 'q', 'consegna', 'ritardo']) sp.delete(k);
      if (vista === 'chiusi') sp.set('stato', ProjectStatus.CHIUSO);
      if (initialDivision) sp.set('divisione', TUTTE);
      else sp.delete('divisione');
      return sp;
    }, { replace: true });
  };

  const nomeClienteFiltro = clientiQ.data?.items.find((c) => c.id === clientId);

  return (
    <div className="pjm">
      <MPage>
        {vista === 'consegne'
          ? <HeroConsegne items={consegneTutte} vede={vedeConsegne} />
          : vista === 'chiusi'
            ? <MHero label="Progetti chiusi" value={chiusi.length} sub={chiusi.length ? 'lavori consegnati: restano qui da consultare' : 'nessun progetto chiuso'} />
            : <HeroInCorso vivi={vivi} caricando={listaQ.isLoading && !listaQ.data} />}

        <div className="pjm-tools-wrap">
          {viste.length > 1 && <MSeg<Vista> value={vista} onChange={scegliVista} options={viste} />}
          {mostraDivisioni && vista !== 'consegne' && (
            <MSeg<string>
              value={divisione || TUTTE}
              onChange={(k) => setDivisione(k === TUTTE ? '' : k)}
              options={[
                { key: TUTTE, label: 'Tutte le divisioni', count: divisionsQ.data?.totale },
                ...divisioniPiene.map((d) => ({ key: d as string, label: DIVISION_LABEL[d], count: conta(d) })),
              ]}
            />
          )}
          <div className="mk-tools pjm-tools">
            <MSearch
              value={search}
              onChange={setSearch}
              placeholder={vista === 'consegne' ? 'Cerca una consegna' : 'Cerca un progetto'}
            />
            {vista !== 'consegne' && <OrdinaFiltra filtri={filtriUi} label="Filtra" />}
          </div>
        </div>

        {clientId && vista !== 'consegne' && (
          <MGroup title="Filtro sul cliente">
            <MRow
              icon="filter"
              title={nomeClienteFiltro ? `Solo ${leggibile(nomeClienteFiltro.shortName || nomeClienteFiltro.legalName)}` : 'Solo un cliente'}
              sub="Tocca per vedere i progetti di tutti i clienti"
              onClick={() => setClientId('')}
              chevron={false}
            />
          </MGroup>
        )}

        {vista === 'consegne' ? (
          <ConsegneMobile
            items={consegneTutte}
            vede={vedeConsegne}
            search={search}
            caricando={consegneQ.isLoading && !consegneQ.data}
            onApri={(id) => navigate(`/projects/${id}`)}
            onLista={() => scegliVista('corso')}
          />
        ) : listaQ.isLoading && !listaQ.data ? (
          <MGroup title="Progetti"><MEmpty>Carico i progetti…</MEmpty></MGroup>
        ) : (vista === 'chiusi' ? chiusi : vivi).length === 0 ? (
          <MGroup title="Progetti">
            <MEmpty>
              {filtriAccesi > 0
                ? 'Nessun progetto con questa ricerca o con questi filtri.'
                : vista === 'chiusi' ? 'Nessun progetto chiuso.' : 'Nessun progetto in corso. I progetti nascono da una trattativa vinta, oppure si aprono dal computer.'}
            </MEmpty>
            {filtriAccesi > 0 && <MMore label="Togli i filtri e la ricerca" onClick={azzera} />}
          </MGroup>
        ) : vista === 'chiusi' ? (
          <MGroup title="Chiusi" count={chiusi.length}>
            {[...chiusi].sort((a, b) => (b.deadline ?? '').localeCompare(a.deadline ?? '')).map((p) => (
              <RigaProgetto key={p.id} p={p} onApri={() => navigate(`/projects/${p.id}`)} />
            ))}
          </MGroup>
        ) : (
          <GruppiPerConsegna vivi={vivi} onApri={(id) => navigate(`/projects/${id}`)} />
        )}

        {canWrite && vista !== 'consegne' && (
          <DesktopOnly
            cosa="L'apertura di un nuovo progetto"
            perche="richiede cliente, offerta, scadenze e responsabilità. Dal telefono si consulta il lavoro e si aggiungono task."
          ><></></DesktopOnly>
        )}
      </MPage>
    </div>
  );
}

function HeroInCorso({ vivi, caricando }: { vivi: ProjectRow[]; caricando: boolean }) {
  if (caricando) return <MHero label="Progetti in corso" value="…" />;
  const late = vivi.filter((p) => p.isLate).length;
  const valore = vivi.reduce((n, p) => n + (p.revenue || 0), 0);
  return (
    <MHero
      label={vivi.length === 1 ? 'Progetto in corso' : 'Progetti in corso'}
      value={vivi.length}
      sub={
        <>
          {late > 0
            ? <span className="is-alert">{late} in ritardo</span>
            : 'nessuno in ritardo'}
          {valore > 0 && <> · in tutto valgono {euro(valore)}</>}
        </>
      }
    />
  );
}

/**
 * In ritardo, entro 30 giorni, più avanti, senza data: gli stessi conti della
 * fascia del computer (il ritardo lo dice il server, i 30 giorni partono da
 * oggi a Roma come lì). Dentro ogni gruppo, la consegna più vicina prima.
 */
function GruppiPerConsegna({ vivi, onApri }: { vivi: ProjectRow[]; onApri: (id: string) => void }) {
  const perData = (a: ProjectRow, b: ProjectRow) => (a.deadline ?? '').localeCompare(b.deadline ?? '');
  const late = vivi.filter((p) => p.isLate).sort(perData);
  const entro30 = vivi.filter((p) => !p.isLate && p.deadline && giorniA(p.deadline) < 30).sort(perData);
  const dopo = vivi.filter((p) => !p.isLate && p.deadline && giorniA(p.deadline) >= 30).sort(perData);
  const senza = vivi.filter((p) => !p.deadline);
  const riga = (p: ProjectRow) => <RigaProgetto key={p.id} p={p} onApri={() => onApri(p.id)} />;
  return (
    <>
      {late.length > 0 && <MGroup title="In ritardo" count={late.length}>{late.map(riga)}</MGroup>}
      {entro30.length > 0 && <MGroup title="Da consegnare entro 30 giorni" count={entro30.length}>{entro30.map(riga)}</MGroup>}
      {dopo.length > 0 && <MGroup title="Più avanti" count={dopo.length}>{dopo.map(riga)}</MGroup>}
      {senza.length > 0 && (
        <MGroup
          title="Senza data di consegna"
          count={senza.length}
          note={senza.length === 1 ? 'Non è in ritardo: non si sa ancora quando chiude.' : 'Non sono in ritardo: non si sa ancora quando chiudono.'}
        >
          {senza.map(riga)}
        </MGroup>
      )}
    </>
  );
}

/**
 * Una riga: in testa la consegna (rossa se è passata), il cliente come titolo
 * (è il modo in cui un progetto si chiama davvero), sotto che cos'è e a che
 * punto è. Il valore non sta nella riga: a 390px la colonna dell'importo
 * tagliava proprio "a che punto è" ("Mese 11 di 12…"); il totale è in cima e
 * quello di ognuno nella scheda.
 */
function RigaProgetto({ p, onApri }: { p: ProjectRow; onApri: () => void }) {
  const fase = p.phase || STATO_PROGETTO[p.status] || '';
  const stato = p.status !== ProjectStatus.IN_CORSO && p.status !== ProjectStatus.CHIUSO ? STATO_PROGETTO[p.status] : null;
  const pezzi = [
    p.isLate ? (p.giorniRitardo === 1 ? '1 giorno di ritardo' : `${p.giorniRitardo} giorni di ritardo`) : null,
    stato,
    cosaE(p.name),
    fase && fase !== stato ? fase : null,
    !p.isLate && p.oltrePromessa ? 'oltre la data promessa' : null,
  ].filter(Boolean);
  return (
    <MRow
      text={p.deadline ? dataInTesta(p.deadline) : '—'}
      tone={p.isLate ? 'alert' : undefined}
      title={nomeCliente(p.client)}
      sub={pezzi.join(' · ')}
      subWrap
      onClick={onApri}
    />
  );
}

// ── Le consegne di Design ─────────────────────────────────────────────────

const STATO_CONSEGNA: Record<string, string> = {
  BRIEF: 'Brief', DRAFT: 'Bozza', REVIEW: 'In revisione', APPROVED: 'Approvata', DELIVERED: 'Consegnata',
};
const TIPO_CONSEGNA: Record<string, string> = {
  DESIGN_SYSTEM: 'Design system', PROTOTYPE: 'Prototipo', BRAND_ASSET: 'Asset brand', SOCIAL_ASSET: 'Asset social',
  WEB_PAGE: 'Pagina web', PRESENTATION: 'Presentazione', OTHER: 'Altro',
};

function HeroConsegne({ items, vede }: { items: DeliverableRow[]; vede: boolean }) {
  if (!vede) return <MHero label="Consegne di Design" value="—" sub="non visibili con il tuo profilo" />;
  const orfane = items.filter((d) => !d.project).length;
  const aperte = items.filter((d) => d.status !== 'DELIVERED').length;
  return (
    <MHero
      label="Consegne di Design ancora aperte"
      value={aperte}
      sub={`${plurale(items.length, 'consegna', 'consegne')} in tutto${orfane ? ` · ${orfane} senza progetto` : ''}`}
    />
  );
}

/**
 * Le consegne raggruppate per stato, dal brief alla consegna. Quelle senza
 * progetto non aprono niente: si vedono solo da qui, come sul computer.
 */
function ConsegneMobile({ items, vede, search, caricando, onApri, onLista }: {
  items: DeliverableRow[];
  vede: boolean;
  search: string;
  caricando: boolean;
  onApri: (projectId: string) => void;
  onLista: () => void;
}) {
  if (!vede) {
    return (
      <MGroup title="Consegne">
        <MEmpty>Servono i permessi della divisione Design. I progetti, quelli sì, sono nella vista In corso.</MEmpty>
        <MMore label="Vai ai progetti in corso" onClick={onLista} />
      </MGroup>
    );
  }
  if (caricando) return <MGroup title="Consegne"><MEmpty>Carico le consegne…</MEmpty></MGroup>;
  const q = search.trim().toLowerCase();
  const visibili = items.filter((d) => !q || `${d.title} ${d.project?.name ?? ''} ${d.owner ? `${d.owner.firstName} ${d.owner.lastName}` : ''}`.toLowerCase().includes(q));
  if (!visibili.length) {
    return <MGroup title="Consegne"><MEmpty>{q ? `Nessuna consegna con "${search.trim()}".` : 'Nessuna consegna. Si aprono dalla scheda del progetto a cui appartengono.'}</MEmpty></MGroup>;
  }
  const ordine = ['BRIEF', 'DRAFT', 'REVIEW', 'APPROVED', 'DELIVERED'];
  const gruppi = ordine
    .map((s) => ({ s, righe: visibili.filter((d) => d.status === s) }))
    .concat([{ s: 'ALTRO', righe: visibili.filter((d) => !ordine.includes(d.status)) }])
    .filter((g) => g.righe.length);
  return (
    <>
      {gruppi.map((g) => (
        <MGroup key={g.s} title={STATO_CONSEGNA[g.s] ?? 'Altre'} count={g.righe.length}>
          {g.righe.map((d) => (
            <MRow
              key={d.id}
              text={d.deadline ? dataInTesta(d.deadline) : '—'}
              tone={d.deadline && d.status !== 'DELIVERED' && giorniA(d.deadline) < 0 ? 'alert' : undefined}
              title={d.title}
              sub={[d.project ? d.project.name : 'nessun progetto', TIPO_CONSEGNA[d.type] ?? d.type, d.owner ? `${d.owner.firstName} ${d.owner.lastName}` : null].filter(Boolean).join(' · ')}
              onClick={d.project ? () => onApri(d.project!.id) : undefined}
            />
          ))}
        </MGroup>
      ))}
    </>
  );
}
